import { events } from '@utils/events';
import { forkJoin, MonoTypeOperatorFunction } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import findIndex from 'lodash-es/findIndex';
import iteratee from 'lodash-es/iteratee';
import times from 'lodash-es/times';
import uniqueId from 'lodash-es/uniqueId';

import { IFBRestoreScrollPosition } from '@interfaces';
import { IFBCollectionQueryParams, IFBCollectionService } from '@app/models/collection';
import { logger } from '@utils/logger.service';
import { NgOnDestroy } from '@app/shared/services/ng-on-destroy.service';
import { storage } from '@utils/storage.service';
import { purchaseStatus } from '@app/shared/enums/purchase-status';
import { ChangeDetectorRef, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Triggers the infinite-scroll directive event to fetch more items
// when there is a visual space available
const FILTERED_COUNT_UPDATED_EVENT = 'collection.filtered-count-updated';

export class Collection<Item, Counts> {
  counts: Counts;
  countsSignal = signal<Counts>(undefined);
  filteredCount: number;
  filteredCountSignal = signal<number>(-1);

  items: { data: Item; frontendId?: string }[];
  itemsSignal = signal<{ data: Item; frontendId?: string }[]>(undefined);
  status: string;
  filters: { value: IFBCollectionQueryParams };

  fetchingItems: boolean;
  fetchingItemsSignal = signal<boolean>(false);
  fetchingMoreItems: boolean;
  isReachedEnd = false;
  isReachedEndSignal = signal<boolean>(false);

  // Don't change order of arguments below, this is not an angular service, it's ES6 class
  constructor(
    private service: IFBCollectionService<Item>,
    private destroy: NgOnDestroy | DestroyRef,
    private resources: { [prop: string]: any[] },
    private cdr?: ChangeDetectorRef
  ) {
    // Count of each status. Contains nothing until fetched
    this.counts = {} as Counts;

    // Filtered count of the current status. Equals -1 until fetched
    this.filteredCount = -1;

    // Fetched items
    this.items = [];
  }

  /**
   * Shorthand to get the count of a current status
   */
  get count() {
    return this.counts[this.status];
  }

  private untilDestroyed<T>(): MonoTypeOperatorFunction<T> {
    return this.destroy instanceof DestroyRef ? takeUntilDestroyed(this.destroy) : takeUntil(this.destroy);
  }

  /**
   * Fetches the items of a new status and filters.
   */
  fetchItems(status, filters?: { value: IFBCollectionQueryParams }) {
    this.fetchingItems = true;
    this.fetchingItemsSignal.set(true);

    // Search tag: restore scroll position
    // This is when collection fetches and restores scroll position
    const restoreScrollPosition: IFBRestoreScrollPosition = storage.getItem('restoreScrollPosition');
    const limitOverride =
      restoreScrollPosition?.fetchLimit ?? (this.service as any)?.multiJobSharingLimit ?? this.service.limit;
    // After we read the object we clear it from storage
    storage.removeItem('restoreScrollPosition');

    const params: IFBCollectionQueryParams = {
      ...filters?.value,
      offset: 0,
      limit: limitOverride
    };

    this.service
      .fetchItems(status, params, this.resources)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: response => {
          this.fetchingItems = false;
          this.fetchingItemsSignal.set(false);
          this.filteredCount = response.filteredCount;
          this.filteredCountSignal.set(this.filteredCount);
          this.filters = filters;
          this.items = response.items.map((data: Item) => this.createItem(data));
          this.itemsSignal.set(this.items);
          this.status = status;

          // Search tag: restore scroll position
          // After fetch we scroll to saved position
          if (restoreScrollPosition) {
            setTimeout(() => {
              const scrollingContainer = document.getElementById('scrolling-container');
              if (scrollingContainer) {
                scrollingContainer.scrollTop = restoreScrollPosition.scrollTop;
              }
            }, 0);
          }

          this.isReachedEnd = this.filteredCount - this.items.length === 0;
          this.isReachedEndSignal.set(this.isReachedEnd);
          this.cdr?.markForCheck();

          setTimeout(() => {
            events.broadcast(FILTERED_COUNT_UPDATED_EVENT);
          }, 0);
        },
        error: (error: { status: number }) => {
          if (error.status !== -1) {
            this.fetchingItems = false;
            this.fetchingItemsSignal.set(false);
            this.cdr?.markForCheck();
          }
        }
      });
  }

  /**
   * Fetches more items of previously set status and filters.
   */
  fetchMoreItems() {
    const limit = Math.min(this.filteredCount - this.items.length, this.service.limit);

    if (this.fetchingItems || this.fetchingMoreItems || this.isReachedEnd) {
      return;
    }

    this.fetchingMoreItems = true;

    const offset = this.items.length;
    const params: IFBCollectionQueryParams = {
      ...this.filters?.value,
      limit,
      offset
    };

    this.service
      .fetchItems(this.status, params, this.resources)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: response => {
          this.fetchingMoreItems = false;

          this.addItems(response, limit);
          this.cdr?.markForCheck();
          setTimeout(() => {
            events.broadcast(FILTERED_COUNT_UPDATED_EVENT);
          }, 0);
        },
        error: () => {
          this.fetchingMoreItems = false;

          // Remove the placeholders:
          this.items.length = offset;
        },
        complete: () => {
          this.cdr?.markForCheck();
          this.fetchingMoreItems = false;
        }
      });
  }

  addItems(response: { filteredCount: number; items: Item[] }, limitArg?: number) {
    const limit = limitArg ?? Math.min(this.filteredCount - this.items.length, this.service.limit);
    // Create and append the placeholders:
    const placeholders = times(limit, () => this.createItem(null));
    this.items.push(...placeholders);

    this.filteredCount = response.filteredCount;
    this.filteredCountSignal.set(this.filteredCount);
    response.items.forEach((data: Item, index: number) => {
      this.extendItem(placeholders[index], data);
    });
    this.itemsSignal.set([...this.items]);
    this.isReachedEnd = this.filteredCount - this.items.length === 0;
    this.isReachedEndSignal.set(this.isReachedEnd);
  }

  /**
   * Fetches total item counts of each status.
   */
  fetchCounts(statuses: string[]) {
    const statusQueries$ = {};
    statuses.forEach(status => {
      statusQueries$[status] = this.service.fetchCount(status);
    });

    forkJoin(statusQueries$)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: (result: Counts) => {
          this.counts = result;
          this.countsSignal.set(result);
          this.cdr?.markForCheck();
        },
        error: e => logger.error('Collection fetchCounts failed:', e)
      });
  }

  /**
   * Handles the state of a collection when an item should be replaced with
   * another one.
   */
  handleItemReplaced(predicate: Partial<Item>, newItem: Item) {
    const index = this.findIndexOfItem(predicate);
    this.extendItem(this.items[index], newItem);
  }

  /**
   * Handles the state of a collection when an item should be transitioned
   * from one status to another.
   */
  handleItemStatusChanged(predicate: Partial<Item>, oldStatus: string, newStatus: string | number) {
    if (this.status === oldStatus) {
      this.handleItemUnfiltered(predicate);
    }
    this.counts[oldStatus]--;
    this.counts[newStatus]++;
  }

  /**
   * Handles the state from AUTO_APPROVED to CANCELLED
   */
  handleRevokeReward(predicate: Partial<Item>) {
    this.handleItemUnfiltered(predicate);
    this.counts[purchaseStatus.AUTO_APPROVED]--;
    this.counts[purchaseStatus.CANCELLED]++;
  }

  /**
   * Handles the state of a collection when an item should be added to a
   * status.
   */
  handleItemStatusAdded(newStatus: string | number) {
    this.counts[newStatus]++;
  }

  /**
   * Handles the state of a collection when an item should be removed from a
   * status.
   */
  handleItemStatusRemoved(predicate: Partial<Item>, oldStatus: string) {
    if (this.status === oldStatus) {
      this.handleItemUnfiltered(predicate);
    }
    this.counts[oldStatus]--;
  }

  /**
   * Handles the state of a collection when an item should be removed from the
   * current filtered view.
   */
  handleItemUnfiltered(predicate: Partial<Item>) {
    const index = this.findIndexOfItem(predicate);
    this.items.splice(index, 1);
    this.itemsSignal.set([...this.items]);
    this.filteredCount--;
    this.filteredCountSignal.set(this.filteredCount);
    events.broadcast(FILTERED_COUNT_UPDATED_EVENT);
  }

  clear() {
    this.items = [];
    this.itemsSignal.set([]);
    this.filteredCount = -1;
    this.filteredCountSignal.set(-1);
    this.isReachedEnd = false;
    this.isReachedEndSignal.set(false);
  }

  private createItem(data: Item) {
    return {
      data,
      frontendId: uniqueId('collection-item-')
    };
  }

  private extendItem(item: { data: Item; frontendId?: string }, data: Item) {
    if (!item) {
      return this.createItem(data);
    }

    item.data = data;
    return item;
  }

  private findIndexOfItem(item: Partial<Item>) {
    const predicate = iteratee(item);
    return findIndex(this.items, item => predicate(item.data));
  }
}
